import React from "react"
import { useTranslation } from "react-i18next"
import { Link, useLocation, useParams } from "react-router-dom"
import { Row, Col, BreadcrumbItem } from "reactstrap"
import { ROUTES } from "constants/routes"
import BackButton from "components/Shared/BackButton"
import {
  getBackFallbackPath,
  shouldShowBackButton,
} from "utils/routeUtils"
import {
  getOrdersBrandLabel,
  isOrdersBrandIdSegment,
  useAssignedBrands,
} from "hooks/useAssignedBrands"

const BREADCRUMB_ROUTE_REDIRECTS = {
  "/superadmin": ROUTES.SA_DASHBOARD,
  "/superadmin/billing": ROUTES.SA_SUBSCRIPTIONS,
}

/** Path segments that should keep acronym casing for labels/translations. */
const BREADCRUMB_SEGMENT_LABELS = {
  faq: "FAQ",
  "faq-details": "FAQ Details",
}

const resolveBreadcrumbHref = (pathnames, index) => {
  const built = `/${pathnames.slice(0, index + 1).join("/")}`
  return BREADCRUMB_ROUTE_REDIRECTS[built] || built
}

const Breadcrumbs = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const pathnames = location.pathname.split("/").filter(x => x)
  const { brands } = useAssignedBrands()
  const {
    bannerId,
    categoryId,
    itemId,
    modifierOptionId,
    modifierItemId,
    couponGroupId,
    couponUsageId,
  } = useParams()
  const dynamicRoutes = ["slides", "menus"] // Define dynamic route segments here
  const dynamicParams = [
    bannerId,
    categoryId,
    itemId,
    modifierOptionId,
    modifierItemId,
    couponGroupId,
    couponUsageId,
  ]

  const formatBreadcrumbLabel = label => {
    const overridden = BREADCRUMB_SEGMENT_LABELS[String(label || "").toLowerCase()]
    if (overridden) return overridden

    // Replace hyphens with spaces and capitalize each word
    return label
      .split("-")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const resolveSegmentLabel = (name, index) => {
    const isOrdersBrandSegment =
      pathnames[0] === "orders" &&
      index === 1 &&
      isOrdersBrandIdSegment(name)

    if (isOrdersBrandSegment) {
      return getOrdersBrandLabel(brands, name, t("Orders"))
    }

    const translationKey = formatBreadcrumbLabel(name)
    const translated = t(translationKey)
    // If i18n returns the key unchanged, keep the formatted label (e.g. FAQ).
    return translated || translationKey
  }

  const shouldSkipSegment = (name, index) => {
    const isOrdersBrandSegment =
      pathnames[0] === "orders" &&
      index === 1 &&
      isOrdersBrandIdSegment(name)

    // Single-brand tenants: hide the brand id crumb (nav already says "Orders")
    if (isOrdersBrandSegment && brands.length <= 1) {
      return true
    }

    return false
  }

  const visibleCrumbs = pathnames
    .map((name, index) => ({ name, index }))
    .filter(({ name, index }) => {
      const isDynamic =
        dynamicRoutes.includes(name) || name.startsWith(":")
      const isHideUrl = dynamicParams.includes(name)
      if (isDynamic || isHideUrl) return false
      if (shouldSkipSegment(name, index)) return false
      return true
    })

  const pageTitleSegment =
    visibleCrumbs[visibleCrumbs.length - 1] ||
    (pathnames[0] === "orders"
      ? { name: "orders", index: 0 }
      : null)

  const pageTitle = pageTitleSegment
    ? resolveSegmentLabel(pageTitleSegment.name, pageTitleSegment.index)
    : t("Dashboard")

  const showBack = shouldShowBackButton(location.pathname)
  const backFallback = getBackFallbackPath(location.pathname)

  return (
    <Row>
      <Col xs={12}>
        <div className="custom_breadcumb page-title-box d-sm-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2 mb-sm-0 page-title-with-back">
            {showBack ? (
              <BackButton fallbackTo={backFallback} />
            ) : null}
            <h4 className="mb-0 font-size-18 lh-1">{pageTitle}</h4>
          </div>
          <div className="page-title-right">
            <ol className="breadcrumb m-0">
              <BreadcrumbItem>
                <Link to="/dashboard">{t("Dashboard")}</Link>
              </BreadcrumbItem>
              {visibleCrumbs.map(({ name, index }, crumbIndex) => {
                const isLast = crumbIndex === visibleCrumbs.length - 1
                const routeTo = resolveBreadcrumbHref(pathnames, index)
                const label = resolveSegmentLabel(name, index)
                return (
                  <BreadcrumbItem key={`${name}-${index}`} active={isLast}>
                    {isLast ? (
                      label
                    ) : (
                      <Link to={routeTo}>{label}</Link>
                    )}
                  </BreadcrumbItem>
                )
              })}
            </ol>
          </div>
        </div>
      </Col>
    </Row>
  )
}

export default Breadcrumbs
